// ============================================================================
//  Cliente de /personal — TypeScript sin framework, compilado con esbuild.
//  Puerto de controlador/app/web/personal.html (4 pestañas), adaptado: el
//  alta de fotocheck la dispara el LECTOR DE OFICINA (num del iBeacon), así
//  que "Vincular en vivo" solo asigna, no da de alta. Ver
//  specs/0001-personal-padron.md y specs/20260813-01-lector-de-oficina.md.
// ============================================================================
import type {
  AssignmentHistoryRow,
  BadgeRow,
  BadgeType,
  StateSnapshot,
  UnassignReason,
  Vehicle,
  Worker,
} from '../../src/shared/types';
import { workerDisplayName } from '../../src/shared/types';
import { formatMac, normalizeMac } from '../../src/common/utils/mac.util';
import { esc, getJson, postJson, toast } from '../shared/api';

declare const io: (() => { on: (event: string, cb: (data: unknown) => void) => void }) | undefined;

function $(id: string): HTMLElement | null {
  return document.getElementById(id);
}
function inputEl(id: string): HTMLInputElement | null {
  return $(id) as HTMLInputElement | null;
}
function val(id: string): string {
  return inputEl(id)?.value ?? '';
}
function onClick(id: string, handler: () => void | Promise<void>): void {
  $(id)?.addEventListener('click', () => {
    void handler();
  });
}
function showModal(id: string): void {
  $(id)?.classList.add('show');
}
function hideModal(id: string): void {
  $(id)?.classList.remove('show');
}

// ==========================================================================
//  Pestañas
// ==========================================================================
const TABS = ['personas', 'vehiculos', 'fotochecks', 'vincular'];

function showTab(tab: string): void {
  const target = TABS.includes(tab) ? tab : 'personas';
  for (const t of TABS) {
    $(`section-${t}`)?.classList.toggle('active', t === target);
    $(`tab-${t}`)?.classList.toggle('active', t === target);
  }
}

$('p-tabs')?.addEventListener('click', (e) => {
  const tabEl = (e.target as HTMLElement).closest<HTMLElement>('.tab[data-tab]');
  if (!tabEl) return;
  const name = tabEl.dataset.tab as string;
  history.replaceState(null, '', `#${name}`);
  showTab(name);
});
window.addEventListener('hashchange', () => showTab(location.hash.slice(1)));

// ==========================================================================
//  Carga
// ==========================================================================
let workers: Worker[] = [];
let vehicles: Vehicle[] = [];
let badges: BadgeRow[] = [];
let retryTimer: ReturnType<typeof setTimeout> | null = null;

async function load(): Promise<void> {
  try {
    [workers, vehicles, badges] = await Promise.all([
      getJson<Worker[]>('/api/personal/workers'),
      getJson<Vehicle[]>('/api/personal/vehicles'),
      getJson<BadgeRow[]>('/api/personal/badges'),
    ]);
    $('loading')?.setAttribute('hidden', '');
    $('content')?.removeAttribute('hidden');
    $('banner')?.classList.remove('show');
    renderWorkers();
    renderVehicles();
    renderBadges();
  } catch {
    $('banner')?.classList.add('show');
    if (retryTimer) clearTimeout(retryTimer);
    retryTimer = setTimeout(() => void load(), 4000);
  }
}

async function loadWorkers(): Promise<void> {
  workers = await getJson<Worker[]>(
    `/api/personal/workers?q=${encodeURIComponent(val('w-search'))}`,
  );
  renderWorkers();
}
async function loadVehicles(): Promise<void> {
  vehicles = await getJson<Vehicle[]>(
    `/api/personal/vehicles?q=${encodeURIComponent(val('v-search'))}`,
  );
  renderVehicles();
}
async function loadBadges(): Promise<void> {
  badges = await getJson<BadgeRow[]>('/api/personal/badges');
  renderBadges();
}

onClick('banner-retry', () => load());

// ==========================================================================
//  Personas
// ==========================================================================
function renderWorkers(): void {
  const rows = $('w-rows');
  if (!rows) return;
  rows.innerHTML = workers
    .map(
      (w) => `
    <tr>
      <td class="mono">${esc(w.dni)}</td>
      <td>${esc(w.name)}</td>
      <td>${esc(w.lastName)}</td>
      <td>${esc(w.role)}</td>
      <td>${esc(w.companyName)}</td>
      <td>${esc(w.area)}</td>
      <td>${w.privilege ? 'Sí' : 'No'}</td>
      <td>${badgeChip(w.badgeMac, w.badgeCode)}</td>
      <td class="row-actions">
        <button type="button" class="btn btn-ghost btn-sm" data-edit-worker="${esc(w.dni)}">Editar</button>
        <button type="button" class="btn btn-ghost btn-sm" data-deactivate-worker="${w.id}">Dar de baja</button>
      </td>
    </tr>`,
    )
    .join('');
  $('w-empty')?.toggleAttribute('hidden', workers.length > 0);

  rows.querySelectorAll<HTMLButtonElement>('[data-edit-worker]').forEach((btn) => {
    btn.addEventListener('click', () => openWorkerModal(btn.dataset.editWorker as string));
  });
  rows.querySelectorAll<HTMLButtonElement>('[data-deactivate-worker]').forEach((btn) => {
    btn.addEventListener(
      'click',
      () => void deactivateWorker(Number(btn.dataset.deactivateWorker)),
    );
  });
}

function badgeChip(mac: string, code: string): string {
  if (!mac) return '<span class="chip chip-dim">sin fotocheck</span>';
  return `<span class="chip chip-ok">${esc(code || formatMac(mac))}</span>`;
}

function openWorkerModal(dni: string): void {
  const w = workers.find((x) => x.dni === dni);
  ($('w-modal-title') as HTMLElement).textContent = w ? 'Editar trabajador' : 'Nuevo trabajador';
  const doc = inputEl('w-doc');
  if (doc) {
    doc.value = w?.dni ?? '';
    doc.readOnly = Boolean(w); // el documento es la clave natural: no se edita
  }
  if (inputEl('w-name')) (inputEl('w-name') as HTMLInputElement).value = w?.name ?? '';
  if (inputEl('w-lastname')) (inputEl('w-lastname') as HTMLInputElement).value = w?.lastName ?? '';
  if (inputEl('w-role')) (inputEl('w-role') as HTMLInputElement).value = w?.role ?? '';
  if (inputEl('w-company')) (inputEl('w-company') as HTMLInputElement).value = w?.companyName ?? '';
  if (inputEl('w-area')) (inputEl('w-area') as HTMLInputElement).value = w?.area ?? '';
  if (inputEl('w-privilege'))
    (inputEl('w-privilege') as HTMLInputElement).checked = w?.privilege ?? false;
  if (inputEl('w-blood')) (inputEl('w-blood') as HTMLInputElement).value = w?.bloodType ?? '';
  if (inputEl('w-medical')) (inputEl('w-medical') as HTMLInputElement).value = w?.medicalExam ?? '';
  const trainings = $('w-trainings') as HTMLTextAreaElement | null;
  if (trainings) trainings.value = (w?.trainings ?? []).join('\n');
  showModal('w-modal');
}

$('w-add')?.addEventListener('click', () => openWorkerModal(''));
onClick('w-cancel', () => hideModal('w-modal'));
onClick('w-save', async () => {
  const ok = await postJson('/api/personal/workers', {
    dni: val('w-doc').trim(),
    name: val('w-name').trim(),
    lastName: val('w-lastname').trim(),
    role: val('w-role').trim(),
    companyName: val('w-company').trim(),
    privilege: ($('w-privilege') as HTMLInputElement | null)?.checked ?? false,
    area: val('w-area').trim(),
    bloodType: val('w-blood').trim(),
    medicalExam: val('w-medical').trim(),
    trainings: val('w-trainings')
      .split('\n')
      .map((t) => t.trim())
      .filter((t) => t.length > 0),
  });
  if (ok) {
    hideModal('w-modal');
    void loadWorkers();
  }
});

async function deactivateWorker(id: number): Promise<void> {
  if (!confirm('¿Dar de baja a este trabajador?')) return;
  if (await postJson('/api/personal/workers/deactivate', { id })) void loadWorkers();
}

let workerSearchTimer: ReturnType<typeof setTimeout> | null = null;
$('w-search')?.addEventListener('input', () => {
  if (workerSearchTimer) clearTimeout(workerSearchTimer);
  workerSearchTimer = setTimeout(() => void loadWorkers(), 250);
});

// ==========================================================================
//  Vehículos
// ==========================================================================
function renderVehicles(): void {
  const rows = $('v-rows');
  if (!rows) return;
  rows.innerHTML = vehicles
    .map(
      (v) => `
    <tr>
      <td class="mono">${esc(v.plate)}</td>
      <td>${esc(v.type)}</td>
      <td>${esc(v.company)}</td>
      <td>${esc(v.driver)}</td>
      <td>${badgeChip(v.badgeMac, v.badgeCode)}</td>
      <td class="row-actions">
        <button type="button" class="btn btn-ghost btn-sm" data-edit-vehicle="${esc(v.plate)}">Editar</button>
        <button type="button" class="btn btn-ghost btn-sm" data-deactivate-vehicle="${v.id}">Dar de baja</button>
      </td>
    </tr>`,
    )
    .join('');
  $('v-empty')?.toggleAttribute('hidden', vehicles.length > 0);

  rows.querySelectorAll<HTMLButtonElement>('[data-edit-vehicle]').forEach((btn) => {
    btn.addEventListener('click', () => openVehicleModal(btn.dataset.editVehicle as string));
  });
  rows.querySelectorAll<HTMLButtonElement>('[data-deactivate-vehicle]').forEach((btn) => {
    btn.addEventListener(
      'click',
      () => void deactivateVehicle(Number(btn.dataset.deactivateVehicle)),
    );
  });
}

function openVehicleModal(plate: string): void {
  const v = vehicles.find((x) => x.plate === plate);
  ($('v-modal-title') as HTMLElement).textContent = v ? 'Editar vehículo' : 'Nuevo vehículo';
  const plateEl = inputEl('v-plate');
  if (plateEl) {
    plateEl.value = v?.plate ?? '';
    plateEl.readOnly = Boolean(v);
  }
  if (inputEl('v-type')) (inputEl('v-type') as HTMLInputElement).value = v?.type ?? '';
  if (inputEl('v-company')) (inputEl('v-company') as HTMLInputElement).value = v?.company ?? '';
  if (inputEl('v-driver')) (inputEl('v-driver') as HTMLInputElement).value = v?.driver ?? '';
  showModal('v-modal');
}

$('v-add')?.addEventListener('click', () => openVehicleModal(''));
onClick('v-cancel', () => hideModal('v-modal'));
onClick('v-save', async () => {
  const ok = await postJson('/api/personal/vehicles', {
    plate: val('v-plate').trim(),
    type: val('v-type').trim(),
    company: val('v-company').trim(),
    driver: val('v-driver').trim(),
  });
  if (ok) {
    hideModal('v-modal');
    void loadVehicles();
  }
});

async function deactivateVehicle(id: number): Promise<void> {
  if (!confirm('¿Dar de baja a este vehículo?')) return;
  if (await postJson('/api/personal/vehicles/deactivate', { id })) void loadVehicles();
}

let vehicleSearchTimer: ReturnType<typeof setTimeout> | null = null;
$('v-search')?.addEventListener('input', () => {
  if (vehicleSearchTimer) clearTimeout(vehicleSearchTimer);
  vehicleSearchTimer = setTimeout(() => void loadVehicles(), 250);
});

// ==========================================================================
//  Fotochecks
// ==========================================================================
const STATUS_LABEL: Record<string, string> = {
  stock: 'En stock',
  assigned: 'Asignado',
  lost: 'Perdido',
  retired: 'De baja',
};
const STATUS_CHIP: Record<string, string> = {
  stock: 'chip-dim',
  assigned: 'chip-ok',
  lost: 'chip-warn',
  retired: 'chip-danger',
};

function filteredBadges(): BadgeRow[] {
  const q = val('b-search').trim().toLowerCase();
  const status = val('b-filter');
  return badges.filter((b) => {
    if (status && b.status !== status) return false;
    if (!q) return true;
    return (
      b.code.toLowerCase().includes(q) ||
      b.mac.toLowerCase().includes(q) ||
      formatMac(b.mac).toLowerCase().includes(q) ||
      b.assignedName.toLowerCase().includes(q)
    );
  });
}

function badgeActions(b: BadgeRow): string {
  const btns: string[] = [
    `<button type="button" class="btn btn-ghost btn-sm" data-history="${b.mac}">Historial</button>`,
  ];
  if (b.status === 'assigned') {
    btns.push(
      `<button type="button" class="btn btn-ghost btn-sm" data-assign="${b.mac}">Reasignar</button>`,
    );
    btns.push(
      `<button type="button" class="btn btn-ghost btn-sm" data-unassign="${b.mac}">Desvincular</button>`,
    );
  } else if (b.status === 'stock') {
    btns.push(
      `<button type="button" class="btn btn-ghost btn-sm" data-assign="${b.mac}">Asignar</button>`,
    );
    btns.push(
      `<button type="button" class="btn btn-ghost btn-sm" data-status="${b.mac}:lost">Perdido</button>`,
    );
    btns.push(
      `<button type="button" class="btn btn-ghost btn-sm" data-status="${b.mac}:retired">Dar de baja</button>`,
    );
    btns.push(
      `<button type="button" class="btn btn-ghost btn-sm" data-delete="${b.mac}">Eliminar</button>`,
    );
  } else {
    btns.push(
      `<button type="button" class="btn btn-ghost btn-sm" data-status="${b.mac}:stock">Reponer a stock</button>`,
    );
    btns.push(
      `<button type="button" class="btn btn-ghost btn-sm" data-delete="${b.mac}">Eliminar</button>`,
    );
  }
  return btns.join('');
}

function renderBadges(): void {
  const rows = $('b-rows');
  if (!rows) return;
  const list = filteredBadges();
  rows.innerHTML = list
    .map(
      (b) => `
    <tr>
      <td>${esc(b.code || '—')}</td>
      <td class="mono">${esc(formatMac(b.mac))}</td>
      <td><span class="chip">${b.type === 'vehicle' ? 'vehículo' : 'persona'}</span></td>
      <td><span class="chip ${STATUS_CHIP[b.status] ?? ''}">${STATUS_LABEL[b.status] ?? b.status}</span></td>
      <td>${esc(b.assignedName || '—')}</td>
      <td class="row-actions">${badgeActions(b)}</td>
    </tr>`,
    )
    .join('');
  $('b-empty')?.toggleAttribute('hidden', list.length > 0);

  rows.querySelectorAll<HTMLButtonElement>('[data-assign]').forEach((btn) => {
    btn.addEventListener('click', () => openAssignModal(btn.dataset.assign as string));
  });
  rows.querySelectorAll<HTMLButtonElement>('[data-unassign]').forEach((btn) => {
    btn.addEventListener('click', () => openUnassignModal(btn.dataset.unassign as string));
  });
  rows.querySelectorAll<HTMLButtonElement>('[data-history]').forEach((btn) => {
    btn.addEventListener('click', () => void openHistoryModal(btn.dataset.history as string));
  });
  rows.querySelectorAll<HTMLButtonElement>('[data-delete]').forEach((btn) => {
    btn.addEventListener('click', () => void deleteBadge(btn.dataset.delete as string));
  });
  rows.querySelectorAll<HTMLButtonElement>('[data-status]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const [mac, status] = (btn.dataset.status as string).split(':');
      void setBadgeStatus(mac as string, status as string);
    });
  });
}

async function setBadgeStatus(mac: string, status: string): Promise<void> {
  if (await postJson('/api/personal/badges/status', { mac, status })) void loadBadges();
}

async function deleteBadge(mac: string): Promise<void> {
  if (!confirm('¿Eliminar este fotocheck? Solo es posible si no tiene historial.')) return;
  if (await postJson('/api/personal/badges/delete', { mac })) void loadBadges();
}

$('b-search')?.addEventListener('input', () => renderBadges());
$('b-filter')?.addEventListener('change', () => renderBadges());

// --- asignar -----------------------------------------------------------------
let assignMac = '';
let assignSubject: BadgeType = 'worker';

function openAssignModal(mac: string): void {
  assignMac = mac;
  assignSubject = 'worker';
  ($('assign-mac') as HTMLElement).textContent = formatMac(mac);
  $('assign-tab-worker')?.classList.add('active');
  $('assign-tab-vehicle')?.classList.remove('active');
  if (inputEl('assign-search')) (inputEl('assign-search') as HTMLInputElement).value = '';
  renderAssignResults();
  showModal('assign-modal');
}

function renderAssignResults(): void {
  const q = val('assign-search').trim().toLowerCase();
  const container = $('assign-results');
  if (!container) return;
  if (assignSubject === 'worker') {
    const list = workers.filter(
      (w) =>
        !q || workerDisplayName(w).toLowerCase().includes(q) || w.dni.toLowerCase().includes(q),
    );
    container.innerHTML = list
      .map(
        (w) => `
      <div class="live-row">
        <div class="live-main">${esc(workerDisplayName(w))} <span class="mono">(${esc(w.dni)})</span></div>
        <button type="button" class="btn btn-save btn-sm" data-pick="${w.id}">Asignar</button>
      </div>`,
      )
      .join('');
  } else {
    const list = vehicles.filter(
      (v) => !q || v.plate.toLowerCase().includes(q) || v.driver.toLowerCase().includes(q),
    );
    container.innerHTML = list
      .map(
        (v) => `
      <div class="live-row">
        <div class="live-main">${esc(v.plate)} <span class="mono">${esc(v.driver)}</span></div>
        <button type="button" class="btn btn-save btn-sm" data-pick="${v.id}">Asignar</button>
      </div>`,
      )
      .join('');
  }
  container.querySelectorAll<HTMLButtonElement>('[data-pick]').forEach((btn) => {
    btn.addEventListener('click', () => void confirmAssign(Number(btn.dataset.pick)));
  });
}

async function confirmAssign(subjectId: number): Promise<void> {
  const ok = await postJson('/api/personal/assign', {
    mac: assignMac,
    subjectType: assignSubject,
    subjectId,
  });
  if (ok) {
    hideModal('assign-modal');
    void loadBadges();
  }
}

$('assign-tab-worker')?.addEventListener('click', () => {
  assignSubject = 'worker';
  $('assign-tab-worker')?.classList.add('active');
  $('assign-tab-vehicle')?.classList.remove('active');
  renderAssignResults();
});
$('assign-tab-vehicle')?.addEventListener('click', () => {
  assignSubject = 'vehicle';
  $('assign-tab-vehicle')?.classList.add('active');
  $('assign-tab-worker')?.classList.remove('active');
  renderAssignResults();
});
$('assign-search')?.addEventListener('input', () => renderAssignResults());
onClick('assign-cancel', () => hideModal('assign-modal'));

// --- desvincular ---------------------------------------------------------------
let unassignMac = '';

function openUnassignModal(mac: string): void {
  unassignMac = mac;
  ($('unassign-mac') as HTMLElement).textContent = formatMac(mac);
  const reasonSel = $('unassign-reason') as HTMLSelectElement | null;
  if (reasonSel) reasonSel.value = 'return';
  $('unassign-force-wrap')?.setAttribute('hidden', '');
  const force = $('unassign-force') as HTMLInputElement | null;
  if (force) force.checked = false;
  showModal('unassign-modal');
}

onClick('unassign-cancel', () => hideModal('unassign-modal'));
onClick('unassign-confirm', async () => {
  const reason = val('unassign-reason') as UnassignReason;
  const force = ($('unassign-force') as HTMLInputElement | null)?.checked ?? false;
  try {
    const res = await fetch('/api/personal/unassign', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ mac: unassignMac, reason, force }),
    });
    if (res.status === 409) {
      const data = (await res.json().catch(() => ({}))) as { error?: string };
      toast(data.error || 'Figura dentro de la mina; confirma para forzar', true);
      $('unassign-force-wrap')?.removeAttribute('hidden');
      return;
    }
    if (!res.ok) {
      const data = (await res.json().catch(() => ({}))) as { error?: string };
      toast(data.error || `error ${res.status}`, true);
      return;
    }
    toast('Fotocheck desvinculado');
    hideModal('unassign-modal');
    void loadBadges();
  } catch {
    toast('Sin conexión con el servidor', true);
  }
});

// --- historial -------------------------------------------------------------------
async function openHistoryModal(mac: string): Promise<void> {
  ($('history-mac') as HTMLElement).textContent = formatMac(mac);
  const rowsEl = $('history-rows');
  if (rowsEl) rowsEl.innerHTML = '<tr><td colspan="4">Cargando…</td></tr>';
  showModal('history-modal');
  try {
    const history = await getJson<AssignmentHistoryRow[]>(
      `/api/personal/history?mac=${encodeURIComponent(mac)}`,
    );
    renderHistory(history);
  } catch {
    if (rowsEl) rowsEl.innerHTML = '<tr><td colspan="4">No se pudo cargar.</td></tr>';
  }
}

function renderHistory(history: AssignmentHistoryRow[]): void {
  const rowsEl = $('history-rows');
  if (!rowsEl) return;
  if (!history.length) {
    rowsEl.innerHTML = '<tr><td colspan="4">Sin historial.</td></tr>';
    return;
  }
  rowsEl.innerHTML = history
    .map(
      (h) => `
    <tr>
      <td>${fmtDate(h.startedAt)}</td>
      <td>${h.endedAt ? fmtDate(h.endedAt) : '<span class="chip chip-ok">vigente</span>'}</td>
      <td>${esc(h.subjectName)}</td>
      <td>${esc(h.endReason ?? '—')}</td>
    </tr>`,
    )
    .join('');
}

onClick('history-close', () => hideModal('history-modal'));

function fmtDate(ts: number): string {
  if (!ts) return '—';
  return new Date(ts * 1000).toLocaleString('es-PE');
}

// ==========================================================================
//  Vincular en vivo — SOLO lo que ve el lector de oficina.
//
//  Antes se alimentaba de snapshot.detected[], que es "lo visto por cualquier
//  escáner": desde acá se podía asignar una baliza que en realidad estaba
//  pasando frente a un lector dentro de mina, a kilómetros del mostrador. Se
//  usa `readers[].seen` del lector marcado como de oficina en
//  /config#escaneres, cuyo TTL además es corto (30 s, "lo que este lector ve
//  AHORA") en vez de los 120 s de detected[].
//
//  Solo repinta si la pestaña está visible (mismo criterio que pollLive() del
//  piloto Python).
// ==========================================================================
function tabActive(): boolean {
  return $('section-vincular')?.classList.contains('active') ?? false;
}

function renderDetected(snapshot: StateSnapshot): void {
  const container = $('lv-rows');
  if (!container || !tabActive() || document.hidden) return;
  const minRssi = Number(val('lv-rssi'));
  const onlyUnassigned = ($('lv-unassigned') as HTMLInputElement | null)?.checked ?? false;

  const office = snapshot.readers.find((r) => r.isEnrollment);
  const emptyMsg = $('lv-empty');
  if (!office) {
    container.innerHTML = '';
    if (emptyMsg) {
      emptyMsg.textContent =
        'No hay ningún lector de oficina configurado. Asígnalo en Configuración → Escáneres → Bocamina: «Oficina (enrolamiento)».';
      emptyMsg.removeAttribute('hidden');
    }
    return;
  }

  const list = office.seen
    .filter((d) => (d.rssi ?? -999) >= minRssi)
    .filter((d) => !onlyUnassigned || !d.assigned)
    .slice(0, 15);

  container.innerHTML = list
    .map((d) => {
      const typeLabel = d.subjectType === 'vehicle' ? 'vehículo' : 'persona';
      const status = d.assigned
        ? `<span class="chip chip-ok">${esc(d.name)}</span>`
        : d.registered
          ? '<span class="chip chip-dim">sin asignar</span>'
          : '<span class="chip chip-warn">nuevo</span>';
      const assignBtn = d.assigned
        ? ''
        : `<button type="button" class="btn btn-save btn-sm" data-live-assign="${d.beaconMac}">Asignar</button>`;
      return `
      <div class="live-row">
        <div class="live-main">
          <b>${esc(d.code || formatMac(d.beaconMac))}</b> · ${typeLabel} ${status}
        </div>
        <span class="live-rssi mono">${d.rssi ?? '—'} dBm</span>
        ${assignBtn}
      </div>`;
    })
    .join('');
  if (emptyMsg) {
    emptyMsg.textContent = office.online
      ? 'El lector de oficina no está viendo ningún fotocheck en este momento.'
      : 'El lector de oficina está sin conexión.';
    emptyMsg.toggleAttribute('hidden', list.length > 0);
  }

  container.querySelectorAll<HTMLButtonElement>('[data-live-assign]').forEach((btn) => {
    btn.addEventListener('click', () =>
      openAssignModal(normalizeMac(btn.dataset.liveAssign as string)),
    );
  });
}

if (typeof io === 'function') {
  const socket = io();
  socket.on('state', (data) => renderDetected(data as StateSnapshot));
} else {
  console.warn('socket.io no disponible: ¿se cargó /socket.io/socket.io.js?');
}

// ==========================================================================
//  Arranque
// ==========================================================================
showTab(location.hash.slice(1));
void load();
